import { forwardRef, useState, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import { Collapsible } from "@xsolla/xui-b2b-collapsible";
import type { AccordionProps } from "./types";

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      items,
      multiple = false,
      defaultOpenKeys = [],
      openKeys: openKeysProp,
      onOpenChange,
      className,
      testID,
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const sizing = theme.sizing.collapsibleB2b();

    const isControlled = openKeysProp !== undefined;
    const [openState, setOpenState] = useState<string[]>(defaultOpenKeys);
    const openKeys = isControlled ? openKeysProp! : openState;

    const handleOpenChange = useCallback(
      (key: string, open: boolean) => {
        let next: string[];
        if (open) {
          next = multiple ? [...openKeys, key] : [key];
        } else {
          next = openKeys.filter((k) => k !== key);
        }
        if (!isControlled) setOpenState(next);
        onOpenChange?.(next);
      },
      [openKeys, multiple, isControlled, onOpenChange]
    );

    return (
      <Box
        ref={ref}
        testID={testID || "accordion"}
        className={className}
        flexDirection="column"
        alignItems="stretch"
        width="100%"
        style={{ gap: sizing.accordionItemGap }}
        {...rest}
      >
        {items.map((item) => (
          <Collapsible
            key={item.key}
            title={item.title}
            caption={item.caption}
            icon={item.icon}
            trailing={item.trailing}
            view="white-surface"
            open={openKeys.includes(item.key)}
            onOpenChange={(open) => handleOpenChange(item.key, open)}
            themeMode={themeMode}
            themeProductContext={themeProductContext}
          >
            {item.content}
          </Collapsible>
        ))}
      </Box>
    );
  }
);

Accordion.displayName = "Accordion";
